import { type Edge, type InternalNode, type ReactFlowState, useStore } from "@xyflow/react";
import { useEffect } from "react";

type EdgePublisherProps = {
	/** Every connector the canvas wants drawn. */
	edges: Edge[];
	/** Receives the connectors whose two endpoints React Flow has measured, as a fresh array. */
	onPublish: (edges: Edge[]) => void;
};

/**
 * The measured nodes, as a key that changes whenever one is added, removed or (re)measured - and
 * stays put while nodes are only dragged, so a drag never republishes the edges.
 */
const selectMeasuredIds = (state: ReactFlowState): string => {
	const ids: string[] = [];
	state.nodeLookup.forEach((node: InternalNode) => {
		if (node.measured.width && node.measured.height && node.internals.handleBounds) {
			ids.push(node.id);
		}
	});

	return ids.sort().join("|");
};

/**
 * Hands the canvas its edges only once React Flow can draw them.
 *
 * React Flow discards an edge offered before both its endpoints are measured, and does not look at
 * it again. The canvas used to guard against that with a gate of its own - closed whenever it
 * replaced nodes, reopened when React Flow reported every node measured - but that gate could fall
 * out of step with React Flow's measuring and hold every edge back for good: the tree drawn with
 * no connectors, until a reload happened to line the two up again.
 *
 * This reads React Flow's own record instead, so there is nothing to fall out of step. An edge is
 * published once both its endpoints are measured, and the whole set is offered afresh whenever the
 * measured nodes change, which also brings back any edge dropped along the way. It lives inside
 * <ReactFlow> because that record is only reachable within its provider. Renders nothing.
 */
const EdgePublisher = ({ edges, onPublish }: EdgePublisherProps) => {
	const measuredIds = useStore(selectMeasuredIds);

	useEffect(() => {
		if (measuredIds === "") {
			return;
		}

		const measured = new Set(measuredIds.split("|"));
		onPublish(edges.filter((edge: Edge) => measured.has(edge.source) && measured.has(edge.target)));
	}, [edges, measuredIds, onPublish]);

	return null;
};

export default EdgePublisher;
